"use client";

/**
 * Booking Wizard V2 - Simple, intuitive multi-step booking process
 * Mobile-first design with clear visual feedback
 */

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { apiFetch } from "@/lib/api";
import type {
  BookingResponse,
  BookingStep,
  CustomerData,
} from "@/types/booking-v2";
import type { ServiceInfo } from "@/types/tenant-landing-v2";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CustomerStep,
  DateTimeStep,
  ReviewStep,
  ServicesStep,
  SuccessStep,
} from "./steps";

interface BookingWizardV2Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantSlug: string;
  tenantPixKey: string | null;
  services: ServiceInfo[];
  preSelectedServiceId?: string;
}

const STEPS: BookingStep[] = [
  {
    id: 1,
    title: "Serviços",
    description: "Escolha o que deseja",
    completed: false,
  },
  {
    id: 2,
    title: "Data e Hora",
    description: "Quando você quer",
    completed: false,
  },
  {
    id: 3,
    title: "Seus Dados",
    description: "Para contato",
    completed: false,
  },
  {
    id: 4,
    title: "Confirmação",
    description: "Revise tudo",
    completed: false,
  },
];

export function BookingWizardV2({
  open,
  onOpenChange,
  tenantSlug,
  tenantPixKey,
  services,
  preSelectedServiceId,
}: BookingWizardV2Props) {
  const [currentStep, setCurrentStep] = useState(1);
  const [steps, setSteps] = useState<BookingStep[]>(STEPS);

  // Split state into smaller pieces to avoid circular references
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedTimeLabel, setSelectedTimeLabel] = useState<string | null>(
    null
  ); // Display label (e.g., "09:00")
  const [selectedTimeISO, setSelectedTimeISO] = useState<string | null>(null); // Full ISO datetime
  const [selectedServices, setSelectedServices] = useState<string[]>(
    preSelectedServiceId ? [preSelectedServiceId] : []
  );
  const [customer, setCustomer] = useState<CustomerData>({
    name: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [feePaymentConfirmed, setFeePaymentConfirmed] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState<BookingResponse | null>(
    null
  );

  // Calculate totals from selected services
  const totalPrice = services
    .filter((s) => selectedServices.includes(s.id))
    .reduce((sum, s) => sum + s.price, 0);

  const totalDuration = services
    .filter((s) => selectedServices.includes(s.id))
    .reduce((sum, s) => sum + s.duration, 0);

  // Reset wizard when modal opens/closes
  useEffect(() => {
    if (open) {
      // Reset when opening to ensure clean state
      setCurrentStep(1);
      setSteps(STEPS);
      setSelectedDate(new Date());
      setSelectedTimeLabel(null);
      setSelectedTimeISO(null);
      setSelectedServices(preSelectedServiceId ? [preSelectedServiceId] : []);
      setCustomer({ name: "", phone: "", email: "", notes: "" });
      setFeePaymentConfirmed(false);
      setBookingResult(null);
      setIsSubmitting(false);
    } else {
      // Also reset when closing to clean up state
      setTimeout(() => {
        setCurrentStep(1);
        setSteps(STEPS);
        setSelectedDate(new Date());
        setSelectedTimeLabel(null);
        setSelectedTimeISO(null);
        setSelectedServices(preSelectedServiceId ? [preSelectedServiceId] : []);
        setCustomer({ name: "", phone: "", email: "", notes: "" });
        setFeePaymentConfirmed(false);
        setBookingResult(null);
        setIsSubmitting(false);
      }, 300);
    }
  }, [open, preSelectedServiceId]);

  const progress = (currentStep / STEPS.length) * 100;

  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return selectedServices.length > 0;
      case 2:
        return selectedDate && selectedTimeISO;
      case 3:
        return (
          customer.name.trim().length >= 2 &&
          customer.phone.replace(/\D/g, "").length >= 8
        );
      case 4: {
        // Check if any selected service requires booking fee
        const selectedServicesList = services.filter((s) =>
          selectedServices.includes(s.id)
        );
        const requiresFee = selectedServicesList.some(
          (s) => s.requiresBookingFee
        );
        // If requires fee, must confirm payment
        return requiresFee ? feePaymentConfirmed === true : true;
      }
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (currentStep === 4) {
      await handleSubmit();
    } else {
      // Mark current step as completed
      setSteps((prev) =>
        prev.map((step) =>
          step.id === currentStep ? { ...step, completed: true } : step
        )
      );
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Ensure we have valid ISO datetime
      if (!selectedTimeISO) {
        throw new Error("Horário não selecionado");
      }

      // Create a clean payload object with no circular references
      // Use the ISO string directly from the selected slot - no need to reconstruct
      const payload = {
        startAt: selectedTimeISO, // Already in correct UTC format from API
        services: selectedServices,
        customer: {
          name: customer.name,
          phone: customer.phone,
          email: customer.email || undefined,
        },
        notes: customer.notes || undefined,
      };

      console.log({ payload });

      const result = await apiFetch<BookingResponse>(
        `/tenant/${tenantSlug}/bookings`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      setBookingResult(result);
      setCurrentStep(5); // Go to success step
      toast.success("Agendamento realizado com sucesso!");
    } catch (err) {
      // Extract error message without logging the error object
      // The error might be a Response object (from apiFetch) which has circular references
      let errorMessage = "Não foi possível finalizar o agendamento";

      // Handle Response object from apiFetch
      if (err && typeof err === "object" && "status" in err && "json" in err) {
        try {
          const response = err as Response;
          const errorData = await response.json();
          errorMessage =
            errorData.message ||
            `Erro ${response.status}: ${response.statusText}`;
        } catch {
          const response = err as Response;
          errorMessage = `Erro ${response.status || "desconhecido"}`;
        }
      }
      // Handle Error object
      else if (err instanceof Error) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 w-full h-full md:h-auto md:max-h-[90vh] md:max-w-3xl max-w-full max-h-full flex flex-col gap-0 md:rounded-lg rounded-none">
        {/* Header with progress */}
        <DialogHeader className="px-4 md:px-6 py-3 md:py-4 border-b shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg md:text-xl font-bold truncate">
                {currentStep === 5
                  ? "Agendamento Confirmado! 🎉"
                  : steps[currentStep - 1]?.title}
              </DialogTitle>
              {currentStep < 5 && (
                <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                  {steps[currentStep - 1]?.description}
                </p>
              )}
            </div>
            {currentStep < 5 && (
              <div className="hidden md:flex items-center gap-2 shrink-0">
                {steps.map((step) => (
                  <div
                    key={step.id}
                    className={`h-2 w-2 rounded-full transition-colors ${
                      step.completed
                        ? "bg-green-500"
                        : step.id === currentStep
                        ? "bg-primary"
                        : "bg-muted"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
          {currentStep < 5 && (
            <div className="mt-3 space-y-1.5">
              <Progress value={progress} className="h-1.5" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  Etapa {currentStep} de {STEPS.length}
                </span>
                <span>{Math.round(progress)}%</span>
              </div>
            </div>
          )}
        </DialogHeader>

        {/* Step Content - Scrollable area */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6 overscroll-contain">
          {currentStep === 1 && (
            <ServicesStep
              services={services}
              selectedIds={selectedServices}
              onSelect={setSelectedServices}
            />
          )}

          {currentStep === 2 && (
            <DateTimeStep
              tenantSlug={tenantSlug}
              selectedDate={selectedDate}
              selectedTime={selectedTimeLabel}
              totalDuration={totalDuration}
              onDateChange={(date: Date | null) => {
                setSelectedDate(date);
                setSelectedTimeLabel(null); // Reset time when date changes
                setSelectedTimeISO(null);
              }}
              onTimeChange={(timeLabel, timeISO) => {
                setSelectedTimeLabel(timeLabel);
                setSelectedTimeISO(timeISO);
              }}
            />
          )}

          {currentStep === 3 && (
            <CustomerStep data={customer} onChange={setCustomer} />
          )}

          {currentStep === 4 && (
            <ReviewStep
              bookingData={{
                selectedDate,
                selectedTime: selectedTimeLabel,
                selectedServices,
                customer,
                totalPrice,
                totalDuration,
                feePaymentConfirmed,
              }}
              services={services}
              tenantPixKey={tenantPixKey}
              onEdit={(step: number) => setCurrentStep(step)}
              onFeePaymentConfirmChange={setFeePaymentConfirmed}
            />
          )}

          {currentStep === 5 && bookingResult && (
            <SuccessStep
              booking={bookingResult}
              tenantSlug={tenantSlug}
              onClose={() => onOpenChange(false)}
            />
          )}
        </div>

        {/* Footer with navigation */}
        {currentStep < 5 && (
          <div className="border-t px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-3 shrink-0 bg-background">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1 || isSubmitting}
              size="lg"
              className="flex-1 md:flex-initial md:min-w-[120px]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>

            <Button
              onClick={handleNext}
              disabled={!canGoNext() || isSubmitting}
              size="lg"
              className="flex-1 md:flex-initial md:min-w-[160px]"
            >
              {isSubmitting ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                  Enviando...
                </>
              ) : currentStep === 4 ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Confirmar
                </>
              ) : (
                <>
                  Continuar
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
